import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import { apiService, InstallmentOption } from "../services/api";
import { BR_STATES } from "./CardForm";
import { formatCurrency, formatPriceShort } from "../utils/formatCurrency";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "16px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "15px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
};

const selectSx = {
  borderRadius: "16px",
  backgroundColor: "rgba(255,255,255,0.05)",
  color: "#ffffff",
  fontSize: "16px",
  "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
  "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
  "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
};

export type PlanType = "monthly" | "annual" | "lifetime";
type PaymentMethod = "credit_card" | "pix";
type AnnualBillingMode = "upfront" | "installments_12";

export interface BillingAddress {
  line_1: string;
  zip_code: string;
  city: string;
  state: string;
}

export interface PlanCheckoutActivationCtx {
  plan: PlanType;
  annualBilling: AnnualBillingMode | null;
  isTrial: boolean;
}

interface PlanCheckoutProps {
  /** Planos exibidos nas abas (default: os três). */
  availablePlans?: PlanType[];
  /** Plano pré-selecionado (ex.: vindo de query param). */
  initialPlan?: PlanType;
  /** Trial pré-marcado (ignorado quando o plano é vitalício). */
  initialTrial?: boolean;
  /** Endereço de cobrança já coletado (ex.: passo 1 do cadastro). Quando
   *  presente, os campos de endereço não são exibidos e o valor é reutilizado. */
  prefilledBilling?: BillingAddress | null;
  /** Disparado quando a assinatura/acesso é criada (cartão) ou ativada (PIX).
   *  O componente NÃO navega nem toca no AuthContext — quem chama decide. */
  onActivated: (
    result: { status: string },
    ctx: PlanCheckoutActivationCtx,
  ) => void;
}

const ALL_PLANS: PlanType[] = ["monthly", "annual", "lifetime"];

/** Valor (em centavos) que o cliente paga NESTE momento, ou null quando nada é
 *  cobrado agora. Trial não é venda: a cobrança pós-trial roda no backend e o
 *  frontend nunca a vê. É o valor mandado pro Meta Pixel como Purchase. */
export function chargedNowCents(
  ctx: PlanCheckoutActivationCtx,
  prices: {
    pix: boolean;
    monthly: number | null;
    annualUpfront: number | null;
    annualPerCycle: number | null;
    lifetimePix: number | null;
    lifetimeCard: number | null;
  },
): number | null {
  if (ctx.plan === "lifetime") {
    return prices.pix ? prices.lifetimePix : prices.lifetimeCard;
  }
  if (ctx.isTrial) return null;
  if (ctx.plan === "annual") {
    return ctx.annualBilling === "installments_12"
      ? prices.annualPerCycle
      : prices.annualUpfront;
  }
  return prices.monthly;
}

export default function PlanCheckout({
  availablePlans = ALL_PLANS,
  initialPlan,
  initialTrial = true,
  prefilledBilling = null,
  onActivated,
}: PlanCheckoutProps) {
  const collectBillingInline = !prefilledBilling;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(
    initialPlan && availablePlans.includes(initialPlan)
      ? initialPlan
      : availablePlans[0],
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("credit_card");

  const [licensesAvailable, setLicensesAvailable] = useState(87);
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);
  const [monthlyAmount, setMonthlyAmount] = useState<number | null>(null);
  const [lifetimeBaseAmount, setLifetimeBaseAmount] = useState<number | null>(null);
  const [selectedInstallments, setSelectedInstallments] = useState(1);

  // Plano anual (à vista / 12x) e trial self-serve.
  const [annualBilling, setAnnualBilling] = useState<AnnualBillingMode>("upfront");
  const [annualUpfront, setAnnualUpfront] = useState<number | null>(null);
  const [annual12xPerCycle, setAnnual12xPerCycle] = useState<number | null>(null);
  const [startTrial, setStartTrial] = useState(initialTrial);
  const [trialDays, setTrialDays] = useState(7);

  const [cardNumber, setCardNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  const [billingZipCode, setBillingZipCode] = useState(prefilledBilling?.zip_code ?? "");
  const [billingLine1, setBillingLine1] = useState(prefilledBilling?.line_1 ?? "");
  const [billingCity, setBillingCity] = useState(prefilledBilling?.city ?? "");
  const [billingState, setBillingState] = useState(prefilledBilling?.state ?? "");
  const [loadingCep, setLoadingCep] = useState(false);

  const [pixData, setPixData] = useState<{ qrCode: string; qrCodeUrl: string; expiresAt: string } | null>(null);
  const [pixCopied, setPixCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    apiService
      .getLifetimeLicenses()
      .then((data) => setLicensesAvailable(data.available))
      .catch(() => {});

    apiService
      .getSubscriptionPricing()
      .then((data) => {
        setMonthlyAmount(data.monthly.amount);
        setLifetimeBaseAmount(data.lifetime.baseAmount);
        setInstallments(data.lifetime.installments);
        if (data.annual) {
          setAnnualUpfront(data.annual.upfront.amount);
          setAnnual12xPerCycle(data.annual.installments12.perCycle);
        }
        if (data.trial) setTrialDays(data.trial.days);
      })
      .catch(() => {});
  }, []);

  // Ponto único de "vendeu": cartão e PIX passam por aqui. Reporta ao Meta
  // Pixel antes de devolver o controle pra quem chamou.
  const activate = useCallback(
    (result: { status: string }, ctx: PlanCheckoutActivationCtx) => {
      const cents = chargedNowCents(ctx, {
        pix: paymentMethod === "pix",
        monthly: monthlyAmount,
        annualUpfront,
        annualPerCycle: annual12xPerCycle,
        lifetimePix: lifetimeBaseAmount,
        lifetimeCard:
          installments.find((i) => i.number === selectedInstallments)?.total ??
          lifetimeBaseAmount,
      });
      if (cents) {
        window.fbq?.("track", "Purchase", {
          value: cents / 100,
          currency: "BRL",
        });
      }
      onActivated(result, ctx);
    },
    [
      onActivated,
      paymentMethod,
      monthlyAmount,
      annualUpfront,
      annual12xPerCycle,
      lifetimeBaseAmount,
      installments,
      selectedInstallments,
    ],
  );

  const stopPix = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    pollingRef.current = null;
    countdownRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopPix();
    };
  }, [stopPix]);

  const fetchAddressByCep = useCallback(async (cep: string) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;

    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) return;

      if (data.localidade) setBillingCity(data.localidade);
      if (data.uf) setBillingState(data.uf);
      if (data.logradouro) setBillingLine1(data.logradouro);
    } catch {
    } finally {
      setLoadingCep(false);
    }
  }, []);

  const startPixPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const sub = await apiService.getSubscription();
        if (sub.status === "active") {
          stopPix();
          activate(
            { status: "active" },
            { plan: "lifetime", annualBilling: null, isTrial: false },
          );
        }
      } catch {
      }
    }, 5000);
  }, [activate, stopPix]);

  const startCountdown = useCallback((expiresAt: string) => {
    if (countdownRef.current) clearInterval(countdownRef.current);

    const updateTime = () => {
      const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        stopPix();
        setPixData(null);
        setError("PIX expirado. Tente novamente.");
      }
    };

    updateTime();
    countdownRef.current = setInterval(updateTime, 1000);
  }, [stopPix]);

  const handlePlanChange = (plan: PlanType) => {
    setSelectedPlan(plan);
    stopPix();
    setPixData(null);
    if (plan !== "lifetime") setPaymentMethod("credit_card");
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleCopyPix = async () => {
    if (!pixData?.qrCode) return;
    try {
      await navigator.clipboard.writeText(pixData.qrCode);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 3000);
    } catch {
      setError("Erro ao copiar o código PIX");
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError("");

      if (selectedPlan === "lifetime" && paymentMethod === "pix") {
        const result = await apiService.createLifetimeAccess({ paymentMethod: "pix" });

        if (result.pix?.qrCode && result.pix?.qrCodeUrl) {
          setPixData(result.pix);
          startCountdown(result.pix.expiresAt);
          startPixPolling();
        } else if (result.status !== "active") {
          setError("Erro ao gerar QR Code PIX. Tente novamente.");
          return;
        }

        if (result.status === "active") {
          activate(result, { plan: "lifetime", annualBilling: null, isTrial: false });
        }
        return;
      }

      const digits = cardNumber.replace(/\s/g, "");
      if (digits.length < 13) {
        setError("Número do cartão inválido");
        return;
      }
      if (!holderName.trim()) {
        setError("Nome no cartão é obrigatório");
        return;
      }
      const [mm, yy] = expiry.split("/");
      if (!mm || !yy || parseInt(mm) < 1 || parseInt(mm) > 12) {
        setError("Validade inválida");
        return;
      }
      if (cvv.length < 3) {
        setError("CVV inválido");
        return;
      }

      if (paymentMethod === "credit_card" && collectBillingInline) {
        const cleanZip = billingZipCode.replace(/\D/g, "");
        if (cleanZip.length !== 8) {
          setError("CEP inválido");
          return;
        }
        if (!billingLine1.trim()) {
          setError("Endereço de cobrança é obrigatório");
          return;
        }
        if (!billingCity.trim()) {
          setError("Cidade é obrigatória");
          return;
        }
        if (!billingState) {
          setError("Estado é obrigatório");
          return;
        }
      }

      const cardToken = await apiService.tokenizeCard({
        number: digits,
        holderName: holderName.trim(),
        expMonth: parseInt(mm),
        expYear: parseInt(yy),
        cvv,
      });

      const billingAddress = {
        line_1: billingLine1.trim(),
        zip_code: billingZipCode.replace(/\D/g, ""),
        city: billingCity.trim(),
        state: billingState,
      };

      const result = selectedPlan === "lifetime"
        ? await apiService.createLifetimeAccess({
            paymentMethod: "credit_card",
            cardToken,
            installments: selectedInstallments,
            billingAddress,
          })
        : await apiService.createSubscription({
            cardToken,
            billingAddress,
            planKind: selectedPlan === "annual" ? "annual" : "monthly",
            annualBilling: selectedPlan === "annual" ? annualBilling : null,
            startTrial,
          });

      activate(result, {
        plan: selectedPlan,
        annualBilling: selectedPlan === "annual" ? annualBilling : null,
        isTrial: selectedPlan !== "lifetime" && startTrial,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao processar pagamento";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const trialCta = `Começar ${trialDays} dias grátis`;

  const plans = {
    monthly: {
      label: "Plano Mensal",
      price: monthlyAmount != null ? formatPriceShort(monthlyAmount) : "—",
      suffix: "/mês",
      badge: "Plano Mensal",
      buttonText: startTrial ? trialCta : "Assinar Agora",
    },
    annual: {
      label: "Plano Anual",
      price:
        annualBilling === "installments_12"
          ? annual12xPerCycle != null
            ? formatPriceShort(annual12xPerCycle)
            : "—"
          : annualUpfront != null
            ? formatPriceShort(annualUpfront)
            : "—",
      suffix: annualBilling === "installments_12" ? "/mês (12x)" : "/ano",
      badge: "Melhor custo-benefício",
      buttonText: startTrial ? trialCta : "Assinar Anual",
    },
    lifetime: {
      label: "Acesso Vitalício",
      price: lifetimeBaseAmount != null ? formatPriceShort(lifetimeBaseAmount) : "—",
      suffix: " único",
      badge: "Pagamento Único",
      buttonText: paymentMethod === "pix" ? "Gerar PIX" : "Comprar Acesso Vitalício",
    },
  };

  const currentPlan = plans[selectedPlan];

  const selectedInstallmentOption = installments.find((i) => i.number === selectedInstallments);

  return (
    <>
      <div className="flex gap-3 mb-6">
        {availablePlans.map((plan) => (
          <button
            key={plan}
            onClick={() => handlePlanChange(plan)}
            className={`flex-1 py-3 px-4 rounded-2xl text-sm font-semibold transition-all duration-300 border ${
              selectedPlan === plan
                ? "bg-saluton/20 border-saluton/50 text-white shadow-lg shadow-saluton/20"
                : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20"
            }`}
          >
            {plans[plan].label}
          </button>
        ))}
      </div>

      {selectedPlan === "annual" && (
        <div className="flex gap-3 mb-4">
          {(["upfront", "installments_12"] as AnnualBillingMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setAnnualBilling(mode)}
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all duration-300 border ${
                annualBilling === mode
                  ? "bg-saluton/20 border-saluton/50 text-white"
                  : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
              }`}
            >
              {mode === "upfront" ? "À vista (1x)" : "Em 12x"}
            </button>
          ))}
        </div>
      )}

      {selectedPlan === "annual" && annualBilling === "installments_12" && (
        <p className="text-white/50 text-xs mb-4 px-1">
          Compromisso de 12 meses: ao cancelar, as parcelas restantes seguem sendo cobradas até completar 12.
        </p>
      )}

      {selectedPlan !== "lifetime" && (
        <label className="flex items-center gap-3 mb-6 px-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={startTrial}
            onChange={(e) => setStartTrial(e.target.checked)}
            className="w-5 h-5 accent-saluton cursor-pointer"
          />
          <span className="text-white/80 text-sm">
            Começar com {trialDays} dias grátis — a 1ª cobrança só ocorre depois do período de teste.
          </span>
        </label>
      )}

      <Paper
        elevation={8}
        sx={{
          background:
            "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
          backdropFilter: "blur(20px)",
          border: "2px solid rgba(226, 114, 91, 0.2)",
          borderRadius: "32px",
          padding: "40px",
          boxShadow:
            "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
        }}
      >
        <Box className="space-y-6">
          {error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "20px",
                background: "rgba(225, 100, 100, 0.15)",
                border: "1px solid rgba(225, 100, 100, 0.3)",
                color: "#e16464",
                fontWeight: 500,
              }}
            >
              {error}
            </Alert>
          )}

          <div className="text-center">
            <div className="inline-block px-4 py-1 bg-saluton/10 rounded-full border border-saluton/30 text-saluton-light text-sm font-medium mb-4">
              {currentPlan.badge}
            </div>
            <Typography
              variant="h2"
              sx={{
                color: "#E2725B",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                fontFamily: "Oxanium, sans-serif",
              }}
            >
              {currentPlan.price}
              <Typography
                component="span"
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "20px",
                  fontWeight: 400,
                  marginLeft: "4px",
                }}
              >
                {currentPlan.suffix}
              </Typography>
            </Typography>
            {selectedPlan === "lifetime" && (
              <div className="mt-4">
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "13px",
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                  }}
                >
                  Apenas 100 licenças disponíveis
                </Typography>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 700,
                    marginTop: "4px",
                    background: "linear-gradient(90deg, #E2725B 0%, #E2725B 40%, #FFD700 50%, #E2725B 60%, #E2725B 100%)",
                    backgroundSize: "200% 100%",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "shimmer 3s ease-in-out infinite",
                    "@keyframes shimmer": {
                      "0%": { backgroundPosition: "100% 0" },
                      "100%": { backgroundPosition: "-100% 0" },
                    },
                  }}
                >
                  {licensesAvailable} de 100 disponíveis
                </Typography>
              </div>
            )}
          </div>

          <div className="space-y-3 py-2">
            {[
              "Diagnósticos assistidos por agentes clínicos",
              "Disponível 24 horas por dia",
              "Histórico completo de conversas",
              ...(selectedPlan === "lifetime" ? ["Acesso vitalício sem renovação"] : []),
              "Resultados exportáveis",
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-saluton/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-saluton"></div>
                </div>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "15px",
                    fontWeight: 500,
                  }}
                >
                  {feature}
                </Typography>
              </div>
            ))}
          </div>

          {selectedPlan === "lifetime" && !pixData && (
            <div className="flex gap-3">
              {(["credit_card", "pix"] as PaymentMethod[]).map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`flex-1 py-3 px-4 rounded-2xl text-sm font-semibold transition-all duration-300 border ${
                    paymentMethod === method
                      ? "bg-saluton/20 border-saluton/50 text-white shadow-lg shadow-saluton/20"
                      : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span>{method === "credit_card" ? "Cartão de Crédito" : "PIX"}</span>
                    {method === "pix" && (
                      <span className="text-xs text-green-400 font-medium">sem juros</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {pixData ? (
            <div className="space-y-4">
              <div className="text-center">
                <Typography sx={{ color: "#ffffff", fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>
                  Escaneie o QR Code ou copie o código
                </Typography>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-xl">
                    <img src={pixData.qrCodeUrl} alt="QR Code PIX" style={{ width: 200, height: 200 }} />
                  </div>
                </div>
                <div
                  onClick={handleCopyPix}
                  className="cursor-pointer bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-all"
                >
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", marginBottom: "4px" }}>
                    Clique para copiar
                  </Typography>
                  <Typography
                    sx={{
                      color: "#ffffff",
                      fontSize: "12px",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      lineHeight: 1.4,
                    }}
                  >
                    {(pixData.qrCode?.length ?? 0) > 80 ? pixData.qrCode?.slice(0, 80) + "..." : pixData.qrCode}
                  </Typography>
                </div>
                {pixCopied && (
                  <Typography sx={{ color: "#4ade80", fontSize: "13px", marginTop: "8px", fontWeight: 500 }}>
                    Código copiado!
                  </Typography>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 py-2">
                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>
                  Aguardando pagamento... {formatTime(timeLeft)}
                </Typography>
              </div>
            </div>
          ) : (
            <>
              {(selectedPlan === "monthly" || selectedPlan === "annual" || paymentMethod === "credit_card") && (
                <div className="space-y-4 pt-2">
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "13px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Dados do cartão
                  </Typography>

                  <TextField
                    fullWidth
                    label="Número do cartão"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    inputProps={{ maxLength: 19, inputMode: "numeric", autoComplete: "cc-number" }}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth
                    label="Nome no cartão"
                    value={holderName}
                    onChange={(e) => setHolderName(e.target.value.toUpperCase())}
                    inputProps={{ autoComplete: "cc-name" }}
                    sx={inputSx}
                  />

                  <div className="flex gap-4">
                    <TextField
                      label="Validade"
                      placeholder="MM/AA"
                      value={expiry}
                      onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                      inputProps={{ maxLength: 5, inputMode: "numeric", autoComplete: "cc-exp" }}
                      sx={{ ...inputSx, flex: 1 }}
                    />
                    <TextField
                      label="CVV"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      inputProps={{ maxLength: 4, inputMode: "numeric", autoComplete: "cc-csc" }}
                      type="password"
                      sx={{ ...inputSx, flex: 1 }}
                    />
                  </div>

                  {selectedPlan === "lifetime" && installments.length > 0 && (
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: "rgba(255,255,255,0.6)", fontSize: "15px", "&.Mui-focused": { color: "#E2725B" } }}>
                        Parcelas
                      </InputLabel>
                      <Select
                        value={selectedInstallments}
                        label="Parcelas"
                        onChange={(e) => setSelectedInstallments(Number(e.target.value))}
                        sx={selectSx}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: "#1a2e38",
                              border: "1px solid rgba(255,255,255,0.1)",
                              borderRadius: "12px",
                              "& .MuiMenuItem-root": {
                                color: "#ffffff",
                                fontSize: "14px",
                                "&:hover": { backgroundColor: "rgba(226, 114, 91, 0.15)" },
                                "&.Mui-selected": { backgroundColor: "rgba(226, 114, 91, 0.25)" },
                              },
                            },
                          },
                        }}
                      >
                        {installments.map((opt) => (
                          <MenuItem key={opt.number} value={opt.number}>
                            {opt.number}x de {formatCurrency(opt.installmentAmount)}
                            {opt.number > 1
                              ? ` (total: ${formatCurrency(opt.total)})`
                              : " (inclui taxa do cartão)"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {selectedPlan === "lifetime" && selectedInstallmentOption && selectedInstallmentOption.hasFee && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                      <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
                        {selectedInstallmentOption.number > 1
                          ? `Total com juros: ${formatCurrency(selectedInstallmentOption.total)}`
                          : `Total com taxa do cartão: ${formatCurrency(selectedInstallmentOption.total)}`}
                      </Typography>
                    </div>
                  )}

                  {paymentMethod === "credit_card" && collectBillingInline && (
                    <>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: "13px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginTop: "8px",
                        }}
                      >
                        Endereço de cobrança
                      </Typography>

                      <TextField
                        fullWidth
                        label="CEP"
                        value={billingZipCode}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                          const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
                          setBillingZipCode(formatted);
                          if (digits.length === 8) fetchAddressByCep(digits);
                        }}
                        inputProps={{ maxLength: 9, inputMode: "numeric" }}
                        sx={inputSx}
                        disabled={loadingCep}
                      />

                      <TextField
                        fullWidth
                        label="Endereço (nº, rua, complemento)"
                        value={billingLine1}
                        onChange={(e) => setBillingLine1(e.target.value)}
                        sx={inputSx}
                      />

                      <div className="flex gap-4">
                        <TextField
                          label="Cidade"
                          value={billingCity}
                          onChange={(e) => setBillingCity(e.target.value)}
                          sx={{ ...inputSx, flex: 2 }}
                        />
                        <FormControl sx={{ flex: 1 }}>
                          <InputLabel sx={{ color: "rgba(255,255,255,0.6)", fontSize: "15px", "&.Mui-focused": { color: "#E2725B" } }}>
                            UF
                          </InputLabel>
                          <Select
                            value={billingState}
                            label="UF"
                            onChange={(e) => setBillingState(e.target.value as string)}
                            sx={selectSx}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: "#1a2e38",
                                  border: "1px solid rgba(255,255,255,0.1)",
                                  borderRadius: "12px",
                                  maxHeight: 200,
                                  "& .MuiMenuItem-root": {
                                    color: "#ffffff",
                                    fontSize: "14px",
                                    "&:hover": { backgroundColor: "rgba(226, 114, 91, 0.15)" },
                                    "&.Mui-selected": { backgroundColor: "rgba(226, 114, 91, 0.25)" },
                                  },
                                },
                              },
                            }}
                          >
                            {BR_STATES.map((uf) => (
                              <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </>
                  )}
                </div>
              )}

              {selectedPlan === "lifetime" && paymentMethod === "pix" && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: 1.6 }}>
                    Ao clicar em "Gerar PIX", um QR Code será gerado para pagamento de{" "}
                    <strong style={{ color: "#E2725B" }}>
                      {lifetimeBaseAmount != null ? formatCurrency(lifetimeBaseAmount) : "—"}
                    </strong>{" "}
                    sem juros.
                    O código expira em 1 hora.
                  </Typography>
                </div>
              )}

              <Button
                onClick={handleSubscribe}
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  background:
                    "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                  borderRadius: "24px",
                  textTransform: "none",
                  fontSize: "18px",
                  fontWeight: 700,
                  padding: "18px 32px",
                  color: "#ffffff",
                  border: "2px solid rgba(226, 114, 91, 0.3)",
                  boxShadow: "0 8px 24px rgba(226, 114, 91, 0.4)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 32px rgba(226, 114, 91, 0.5)",
                    background:
                      "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
                  },
                  "&:disabled": {
                    background: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={28} sx={{ color: "#ffffff" }} />
                ) : (
                  currentPlan.buttonText
                )}
              </Button>
            </>
          )}

          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "11px",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            {paymentMethod === "credit_card" || selectedPlan !== "lifetime"
              ? "Seus dados de cartão são enviados diretamente ao Pagar.me e nunca passam pelos nossos servidores. Pagamento processado de forma segura."
              : "Pagamento via PIX processado de forma segura pelo Pagar.me."}
          </Typography>
        </Box>
      </Paper>
    </>
  );
}
